import { useCallback, useRef } from 'react';

interface UseResizablePanelOptions {
  /** Current panel width in pixels */
  panelSize: number;
  /** Function to update the panel width in pixels */
  setPanelSize: (px: number) => void;
  /** Which edge the resize handle is on - determines resize direction */
  edge: 'left' | 'right';
  /** Minimum panel width in pixels (default: 280) */
  minSize?: number;
  /** Maximum panel width in pixels (default: 900) */
  maxSize?: number;
}

interface UseResizablePanelReturn {
  /** Mouse down handler for the resize handle */
  handleResizeStart: (e: React.MouseEvent) => void;
  /** Ref indicating if resize is in progress - useful for preventing hover-off during resize */
  isResizingRef: React.MutableRefObject<boolean>;
  /** Current panel width in pixels (clamped to min/max) */
  panelWidth: number;
}

// Hook for creating resizable panels with mouse drag support.
export function useResizablePanel({
  panelSize,
  setPanelSize,
  edge,
  minSize = 280,
  maxSize = 900,
}: UseResizablePanelOptions): UseResizablePanelReturn {
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const panelWidth = Math.max(minSize, Math.min(maxSize, panelSize));

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = panelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) return;

        // Calculate delta based on which edge the handle is on
        // Left edge: moving left (negative delta) increases width
        // Right edge: moving right (positive delta) increases width
        const deltaPixels =
          edge === 'left'
            ? startXRef.current - moveEvent.clientX
            : moveEvent.clientX - startXRef.current;

        const newWidth = Math.max(
          minSize,
          Math.min(maxSize, startWidthRef.current + deltaPixels),
        );
        setPanelSize(newWidth);
      };

      const handleMouseUp = () => {
        isResizingRef.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [panelSize, setPanelSize, edge, minSize, maxSize],
  );

  return {
    handleResizeStart,
    isResizingRef,
    panelWidth,
  };
}
